import React from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.getevo.app";

type PageState =
  | { status: "loading" }
  | { status: "confirm"; maskedEmail: string }
  | { status: "invalid" }
  | { status: "submitting" }
  | { status: "error"; message: string };

function Logo() {
  return (
    <a href="/" className="flex items-center gap-2 no-underline">
      <span className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[20px] tracking-[-0.4px]">
        evo
      </span>
    </a>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-[24px] shadow-[0px_8px_32px_rgba(0,0,0,0.08)] p-8 w-full max-w-[440px] relative">
      <div
        aria-hidden="true"
        className="absolute border border-[rgba(0,6,20,0.05)] border-solid inset-0 pointer-events-none rounded-[24px]"
      />
      {children}
    </div>
  );
}

export default function Unsubscribe() {
  const token = React.useMemo(
    () => new URLSearchParams(window.location.search).get("token") || "",
    [],
  );

  const [page, setPage] = React.useState<PageState>({ status: "loading" });

  React.useEffect(() => {
    if (!token) {
      setPage({ status: "invalid" });
      return;
    }

    fetch(
      `${API_BASE}/api/v1/email/unsubscribe?token=${encodeURIComponent(token)}`,
    )
      .then(async (res) => {
        if (!res.ok) {
          setPage({ status: "invalid" });
          return;
        }
        const data = await res.json();
        if (!data.masked_email) {
          setPage({ status: "invalid" });
          return;
        }
        setPage({ status: "confirm", maskedEmail: data.masked_email });
      })
      .catch(() => setPage({ status: "invalid" }));
  }, [token]);

  const handleConfirm = React.useCallback(async () => {
    if (page.status !== "confirm") return;
    setPage({ status: "submitting" });

    try {
      const res = await fetch(`${API_BASE}/api/v1/email/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        setPage({
          status: "error",
          message: "Something went wrong. Please try again.",
        });
        return;
      }
      window.location.href = "/unsubscribe/success";
    } catch {
      setPage({
        status: "error",
        message: "Something went wrong. Please try again.",
      });
    }
  }, [page, token]);

  return (
    <div className="min-h-screen bg-[#faf9fb] flex flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {page.status === "loading" && (
          <Card>
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#07001a] border-t-transparent rounded-full animate-spin" />
            </div>
          </Card>
        )}

        {page.status === "invalid" && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              This link is no longer valid
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed">
              The unsubscribe link you followed has expired or is invalid. If
              you'd like to manage your email preferences, please{" "}
              <a
                href="mailto:hello@evograding.com"
                className="text-[#07001a] underline"
              >
                contact us
              </a>
              .
            </p>
          </Card>
        )}

        {(page.status === "confirm" || page.status === "submitting") && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              Unsubscribe from emails
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed mb-6">
              We'll stop sending marketing emails to{" "}
              <span className="font-semibold text-[#07001a]">
                {page.status === "confirm" ? page.maskedEmail : ""}
              </span>
              . You'll still receive important account and transactional
              messages.
            </p>
            <button
              onClick={handleConfirm}
              disabled={page.status === "submitting"}
              className="w-full bg-[#07001a] text-white font-['Figtree',sans-serif] font-semibold text-[15px] py-3 rounded-[12px] transition-opacity disabled:opacity-50 hover:opacity-90 active:opacity-80"
            >
              {page.status === "submitting"
                ? "Unsubscribing…"
                : "Confirm unsubscribe"}
            </button>
          </Card>
        )}

        {page.status === "error" && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              Something went wrong
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed mb-6">
              {page.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-[#07001a] text-white font-['Figtree',sans-serif] font-semibold text-[15px] py-3 rounded-[12px] hover:opacity-90 active:opacity-80 transition-opacity"
            >
              Try again
            </button>
          </Card>
        )}
      </main>

      <footer className="text-center pb-8">
        <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[13px]">
          evograding.com &middot;{" "}
          <a
            href="mailto:hello@evograding.com"
            className="underline text-[#776f7b]"
          >
            hello@evograding.com
          </a>
        </p>
      </footer>
    </div>
  );
}
